// ── Input model types (mirror Pydantic models) ────────────────────────────────

export type VehicleTypeValue =
  | 'mainstream_sedan'
  | 'mainstream_suv'
  | 'luxury_sedan'
  | 'luxury_suv'
  | 'performance_sports'
  | 'pickup_truck'
  | 'ev_mainstream'
  | 'ev_luxury_performance'
  | 'exotic_high_performance'
  | 'commercial_van';

export type VocationProfile =
  | 'local_delivery'
  | 'regional_haul'
  | 'long_haul'
  | 'urban_transit'
  | 'vocational_heavy'
  | 'construction'
  | 'passenger_transport';

export type MpgDataSource =
  | 'epa'
  | 'manufacturer'
  | 'gem_baseline'
  | 'fleet_dna_vocation'
  | 'user_override';

export type ResidualDataSource =
  | 'iseecars'
  | 'argonne_anl'
  | 'user_override'
  | 'estimated_class_curve';

export const VEHICLE_TYPE_LABELS: Record<VehicleTypeValue, string> = {
  mainstream_sedan: 'Mainstream Sedan / Hatchback',
  mainstream_suv: 'Mainstream SUV / Crossover',
  luxury_sedan: 'Luxury Sedan',
  luxury_suv: 'Luxury SUV',
  performance_sports: 'Performance / Sports Car',
  pickup_truck: 'Full-Size Pickup Truck',
  ev_mainstream: 'Electric Vehicle (Mainstream)',
  ev_luxury_performance: 'Electric Vehicle (Luxury or Performance)',
  exotic_high_performance: 'Exotic / High Performance',
  commercial_van: 'Commercial Van',
};

export interface DepreciationInputs {
  purchase_price: number;
  down_payment: number;
  residual_value_year_5: number;
  annual_mileage: number;
  depreciation_rate_override: number | null;
}

export interface FinancingInputs {
  loan_term_months: number;
  annual_interest_rate: number; // decimal, e.g. 0.065
  loan_amount: number;
  monthly_payment: number | null;
}

export interface OperatingCostInputs {
  fuel_type: 'gasoline' | 'diesel' | 'electric' | 'phev';
  fuel_cost_per_gallon: number | null;
  mpg: number | null;
  wh_per_mile: number | null;              // stored as Wh/mile e.g. 280 for a typical EV
  electricity_cost_per_kwh: number | null; // stored in dollars
  annual_maintenance_cost: number;
  // Tires — simplified
  tire_replacement_interval_miles: number; // fallback
  tire_set_cost: number;
  tire_interval_months: number | null;     // preferred; overrides miles field
  // Tires — staggered
  is_staggered_tires: boolean;
  front_tire_interval_months: number | null;
  front_tire_set_cost: number | null;
  rear_tire_interval_months: number | null;
  rear_tire_set_cost: number | null;
  mount_balance_per_event: number;
  // Insurance & fees
  annual_insurance_premium: number;
  annual_registration_fee: number;
  ev_registration_surcharge: number | null;
  sales_tax_rate: number; // decimal, e.g. 0.08
  annual_parking_cost: number;
  annual_tolls_cost: number;
  // Warranty & repairs
  out_of_warranty_repair_annual: number;
  warranty_remaining_months: number;
  // Tire wear offset (derived, not user-entered)
  tire_age_months?: number;
  // Optional user override for tire age in months at purchase
  tire_age_override?: number;
  // Winter tires (Item 9)
  has_winter_tires?: boolean;
  winter_tire_set_cost?: number;
  winter_tire_interval_months?: number;
  winter_miles_pct?: number;
  // Premium fuel indicator (set from EPA data)
  requires_premium?: boolean;
}

export interface EmotionalScores {
  driving_enjoyment: number;    // 0-5
  powertrain_character: number; // 0-5
  practical_utility: number;    // 0-5
  winter_confidence: number;    // 0-5
  brand_trust: number;          // 0-5
  status_prestige: number;      // 0-5
  ownership_pride: number;      // 0-5
  technology_features: number;  // 0-5
}

export interface EmotionalWeights {
  driving_enjoyment: number;
  powertrain_character: number;
  practical_utility: number;
  winter_confidence: number;
  brand_trust: number;
  status_prestige: number;
  ownership_pride: number;
  technology_features: number;
}

export interface EmotionalInputs {
  scores: EmotionalScores;
  weights: EmotionalWeights;
}

export interface CapitalAllocationInputs {
  opportunity_cost_rate: number;    // decimal, e.g. 0.07
  analysis_horizon_years: number;   // deprecated: use scenario-level horizon_months
  include_opportunity_cost: boolean;
}

export interface VehicleInput {
  vehicle_id: string;
  label: string;
  model_year: number | null;
  purchase_condition: 'new' | 'used';
  vehicle_type: VehicleTypeValue | null;
  depreciation: DepreciationInputs;
  financing: FinancingInputs;
  operating_costs: OperatingCostInputs;
  emotional: EmotionalInputs;
  capital_allocation: CapitalAllocationInputs;

  // ── Phase 21A: Fleet / operations inputs ────────────────────────────────
  upfit_cost: number;               // wired into acquisition cost in 21A
  upfit_description: string;        // optional, max 200 chars
  vocation_profile: VocationProfile | null;
  loaded_hourly_labor_rate: number; // persist only in 21A (calc in 21G)
  weekly_hours: number;             // persist only in 21A
  weeks_per_year: number;           // default 50
  operating_days_per_week: number;  // 1-7, default 5
  drivers_per_vehicle: number;      // 1-4, default 1
  ownership_period_years: number;   // 1.0-15.0, default 5.0
  annual_miles_per_vehicle: number | null; // fleet-mode per-vehicle override
  // Phase 21G: idle hours override and powertrain-aware fuel pricing
  daily_idle_hours_override: number | null;
  mpg_data_source: MpgDataSource | null;
  electricity_rate_per_kwh: number;        // EV/PHEV charging rate, default 0.16
  diesel_price_per_gallon: number;         // diesel and diesel idle, default 4.00
  charging_infrastructure_cost: number;    // EV one-time capex, default 0
  purchase_incentives: number;             // tax credits/rebates, default 0
  expected_residual_value: number | null;
  residual_data_source: ResidualDataSource | null;
  depreciation_curve_id: string | null;
  // Phase 23: Section 179 election toggle. Default true; rare to set false.
  sec179_elected?: boolean;
  // Phase 26.5.2: dual warranty + 3-zone repair + custom flat cost.
  // null on any field means "use manufacturer / class default at compute time".
  btb_warranty_years?: number | null;
  btb_warranty_miles?: number | null;
  powertrain_warranty_years?: number | null;
  powertrain_warranty_miles?: number | null;
  annual_repair_cost?: number | null;
  custom_annual_cost?: number | null;
  custom_annual_cost_label?: string | null;
}

export type ScenarioMode = 'individual' | 'fleet';

export interface LineItem {
  vehicle: VehicleInput;
  quantity: number;
  line_item_name: string | null;
}

export interface ScenarioInput {
  scenario_id: string | null;
  name: string;
  description: string | null;
  horizon_months: number;         // scenario-level ownership horizon
  annual_miles: number;           // default annual miles for all vehicles
  financial_weight: number;       // 0–1, financial weight in combined ranking
  emotional_weight: number;       // 0–1, emotional weight in combined ranking
  dominance_epsilon_pct: number;  // 0–1, emotional applies within this fraction (0.25 = 25%)
  use_emotional_override?: boolean; // explicit boolean: always apply emotional regardless of cost gap
  investment_return_rate: number; // annual return on capital not invested in vehicle (e.g. 0.07)
  sales_tax_rate: number;         // decimal e.g. 0.08; injected into all vehicles at compute time
  winter_miles_pct: number;       // fraction of annual miles on winter tires (injected when has_winter_tires)
  state_code: string;             // US state abbreviation (e.g. "MI") for geographic defaults
  vehicles: VehicleInput[];
  // Phase 22: heterogeneous fleet support
  mode?: ScenarioMode;            // 'individual' (default) or 'fleet'
  fleet_name?: string | null;
  line_items?: LineItem[] | null; // canonical in fleet mode; lifted from `vehicles` otherwise
  // Phase 22.7: scenario-level fuel-price defaults. Resolution priority:
  //   per-vehicle override > scenario default > EIA cached default.
  // null = unset; per-vehicle prices remain authoritative.
  scenario_fuel_cost_per_gallon?: number | null;
  scenario_diesel_price_per_gallon?: number | null;
  scenario_electricity_rate_per_kwh?: number | null;
  // Phase 23: scenario-level federal tax inputs for Section 179 + bonus
  // depreciation. All four are required on the backend with defaults; nullable
  // on the wire for backward compatibility with pre-1.37 scenarios.
  marginal_tax_rate?: number;        // decimal, 0–0.99 (0.21 = C-corp default)
  business_use_pct?: number;         // 0–1.0 (1.0 = 100%)
  tax_year?: number;                 // 2024–2030
  sec179_limit?: number;             // dollars; default 1,160,000
  // Phase 26.5: scenario-level YoY cost-model controls.
  apply_maintenance_aging?: boolean;             // default true
  fuel_inflation_rate_pct?: number | null;       // null = research default, 0 = disabled
  insurance_stepup_rate_pct?: number | null;     // null = research default, 0 = disabled
}

export type InsuranceDistributionMethod = 'by_acquisition_cost' | 'even_split';

// Phase 22: fleet TCO response shape (POST /api/fleet-tco)

// Phase 22.3: per-year cumulative cost time series.
export interface CumulativeYearPoint {
  year: number;
  cumulative_cost: number;        // gross: acquisition + ops × N
  residual_at_year_end: number;
  net_cost: number;               // cumulative_cost − residual_at_year_end
}

export interface FleetSubtotal {
  acquisition_cost: number;
  annual_fuel_cost: number;
  annual_idle_fuel_cost: number;
  annual_labor_cost: number;
  annual_maintenance_cost: number;
  annual_insurance_cost: number;
  annual_registration_cost: number;
  annual_operating_cost: number;
  // Phase 22.3: explicit subtotal of fuel + labor + maint + ins + reg.
  total_annual_operating_cost: number;
  // Phase 21H: Argonne ANL/ESD-22/2 residual reclamation, scaled by quantity.
  // Subtracted from tco_over_period.
  residual_value_reclaimed: number;
  // Phase 23: scaled-by-quantity tax benefit (Section 179 + bonus depreciation).
  // Subtracted from tco_over_period.
  tax_benefit?: number;
  tco_over_period: number;
  // Phase 22.3: lifetime miles (annual × ownership × quantity).
  lifetime_miles: number;
}

export interface FleetLineOutput {
  line_item_name: string;
  quantity: number;
  vehicle_label: string;
  per_vehicle_tco: Record<string, unknown>;
  subtotal_tco: FleetSubtotal;
}

export interface FleetTotals {
  total_acquisition_cost: number;
  total_annual_fuel_cost: number;
  total_annual_idle_fuel_cost: number;
  total_annual_labor_cost: number;
  total_annual_maintenance_cost: number;
  total_annual_insurance_cost: number;
  total_annual_registration_cost: number;
  total_annual_operating_cost: number;
  total_residual_reclaimed: number;          // Phase 21H
  total_tax_benefit?: number;                // Phase 23
  total_tco_over_period: number;
  vehicle_count: number;
  ownership_period_years: number;
  // Phase 22.3 analytical metrics
  total_lifetime_miles: number;
  fleet_cost_per_mile: number;
  fleet_cost_per_vehicle_per_year: number;
  fleet_fuel_pct_of_tco: number;
  fleet_labor_pct_of_tco: number;
  total_annual_operating_cost_explicit: number;
  total_tco_over_period_annualized: number;
  total_acquisition_cost_annualized: number;
  cumulative_cost_by_year: CumulativeYearPoint[];
  // Phase 26.5: per-year aged/inflated breakdown (fleet aggregate × quantity).
  yearly_costs?: YearlyCostBreakdown[];
}

// Phase 26.5: shape of a single entry in vehicle.yearly_costs (personal)
// and fleet_totals.yearly_costs (fleet). All values are in dollars except
// the year index.
export interface YearlyCostBreakdown {
  year: number;
  fuel: number;
  idle_fuel: number;
  maintenance: number;
  insurance: number;
  registration: number;
  labor: number;
  depreciation: number;
  total: number;
}

export interface FleetMetadata {
  line_item_count: number;
  fuel_type_distribution: Record<string, number>;
  vocation_distribution: Record<string, number>;
  mpg_data_quality: Record<string, number>;
  // Phase 21H.1: argonne_anl_pdf | argonne_synthesis | iseecars_fallback | user_override
  residual_data_source_distribution: Record<string, number>;
  body_styles: string[];
  skipped_zero_quantity_line_items: string[];
  // Phase 22.3: ownership period consistency across line items.
  ownership_period_consistency: 'uniform' | 'mixed';
  ownership_period_range: { min: number; max: number };
}

export interface FleetTCOResponse {
  line_items: FleetLineOutput[];
  fleet_totals: FleetTotals;
  fleet_metadata: FleetMetadata;
}

// Phase 22.3: scenario comparison response (POST /api/scenario-comparison)

export interface TopDriver {
  category: string;        // acquisition | fuel | idle_fuel | labor | maintenance | insurance | registration | residual
  dollar_delta: number;
  pct_of_total_delta: number;
  a_lifetime: number;
  b_lifetime: number;
}

export interface BreakEvenInfo {
  break_even_year: number | null;
  applicable: boolean;
  cumulative_costs_by_year: { year: number; scenario_a: number; scenario_b: number }[];
  rationale: string;
}

export interface SensitivityTestResult {
  name: string;            // e.g. "Fuel +20%"
  kind: 'fuel' | 'mileage';
  multiplier: number;
  scenario_a_total_tco: number;
  scenario_b_total_tco: number;
  headline_delta: number;
  winner: 'a' | 'b' | 'tie';
  winner_flipped: boolean;
}

export interface OwnershipPeriodInfo {
  scenario_a_consistency: 'uniform' | 'mixed';
  scenario_a_avg_years: number;
  scenario_a_range: { min: number; max: number } | null;
  scenario_b_consistency: 'uniform' | 'mixed';
  scenario_b_avg_years: number;
  scenario_b_range: { min: number; max: number } | null;
  both_uniform_and_equal: boolean;
}

export interface ScenarioComparisonResponse {
  result_a: FleetTCOResponse;
  result_b: FleetTCOResponse;
  comparison: {
    headline_delta: { a: number; b: number; dollar_delta: number; pct_delta: number };
    top_drivers: TopDriver[];
    break_even: BreakEvenInfo;
    sensitivity_tests: SensitivityTestResult[];
    ownership_period: OwnershipPeriodInfo;
  };
}

// ── Geographic defaults API response types ──────────────────────────────────

export interface RegistrationResult {
  base_fee: number;
  ev_surcharge: number;
  total: number;
  method: string;
  note: string;
}

export interface GeographicDefaultsResponse {
  state: string;
  sales_tax_rate: number;
  sales_tax_cap: number | null;
  electricity_rate_cents_kwh: number;
  registration: RegistrationResult;
}

export interface GasPricesResponse {
  regular: number;
  premium: number;
  spread: number;
  weeks_averaged: number;
  source: string;
  is_fallback?: boolean;
}

// ── Vehicle profile suggestion response ───────────────────────────────────────

export interface VehicleProfileSuggestion {
  vehicle_type: string;
  suggested_residual_value: number;
  suggested_residual_pct: number;
  suggested_maintenance_annual: number;
  suggested_repair_annual: number;
  curve_type: string;
  volatility_sigma: number;
  horizon_months: number;
}

// ── Market rates response ──────────────────────────────────────────────────────

export interface MarketRatesResponse {
  rate_available: boolean;
  new_car_rate: number | null;
  used_car_rate: number | null;
  rate: number | null;  // legacy
  series_id: string;
  description: string;
  source: string;
  cached: boolean;
}

// ── API response types ────────────────────────────────────────────────────────

export interface ScenarioSummary {
  id: string | number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  // Phase 22.4: surfaced by the list endpoint so cards can render a mode badge
  // without a per-row detail fetch. Defaults to "individual" for legacy rows.
  mode?: ScenarioMode;
}

export interface ScenarioDetail {
  id: string | number;
  name: string;
  description: string | null;
  scenario: ScenarioInput;
  created_at: string;
  updated_at: string;
}

export interface ScenarioCreateResponse {
  id: string | number;
  name: string;
  description: string | null;
  scenario_id: string;
  created_at: string;
  updated_at: string;
}

// ── Compute response types ────────────────────────────────────────────────────

export interface RankEntry {
  vehicle_id: string;
  label: string;
  rank: number;
  score: number;
}

export interface SensitivityPoint {
  apr_delta_pct: number;
  residual_multiplier: number;
  tco_total: number;
  residual_value: number;  // Item 4: actual dollar residual for this multiplier
}

export interface OperatingBreakdown {
  fuel_cost_total: number;
  insurance_total: number;
  registration_total: number;
  maintenance_total: number;
  tire_cost_total: number;
  repair_cost_total: number;
}

export interface VehicleSensitivity {
  vehicle_id: string;
  sensitivity_grid: SensitivityPoint[];
}

export interface VehicleResult {
  vehicle_id: string;
  label: string;
  depreciation: Record<string, unknown>;
  financing: Record<string, unknown>;
  operating: Record<string, unknown>;
  tco: Record<string, unknown>;
  emotional: Record<string, unknown> | null;
  capital: Record<string, unknown> | null;
  // Phase 26.5: per-year cost breakdown with aging + inflation applied.
  yearly_costs?: YearlyCostBreakdown[];
}

export interface ComputeResponse {
  vehicles: VehicleResult[];
  financial_ranking: RankEntry[];
  emotional_ranking: RankEntry[];
  combined_ranking: RankEntry[];
  decision_confidence: number;
  sensitivity: VehicleSensitivity[];
}

// ── Default factory functions ─────────────────────────────────────────────────

export function defaultDepreciationInputs(): DepreciationInputs {
  return {
    purchase_price: 0,
    down_payment: 0,
    residual_value_year_5: 0,
    annual_mileage: 12000,
    depreciation_rate_override: null,
  };
}

export function defaultFinancingInputs(): FinancingInputs {
  return {
    loan_term_months: 60,
    annual_interest_rate: 0.065,
    loan_amount: 30000,
    monthly_payment: null,
  };
}

export function defaultOperatingCostInputs(): OperatingCostInputs {
  return {
    fuel_type: 'gasoline',
    fuel_cost_per_gallon: 0,
    mpg: 30,
    wh_per_mile: 280,
    electricity_cost_per_kwh: null,
    annual_maintenance_cost: 1200,
    tire_replacement_interval_miles: 40000,
    tire_set_cost: 600,
    tire_interval_months: 40,
    is_staggered_tires: false,
    front_tire_interval_months: null,
    front_tire_set_cost: null,
    rear_tire_interval_months: null,
    rear_tire_set_cost: null,
    mount_balance_per_event: 0,
    annual_insurance_premium: 0,
    annual_registration_fee: 150,
    ev_registration_surcharge: null,
    sales_tax_rate: 0.08,
    annual_parking_cost: 0,
    annual_tolls_cost: 0,
    out_of_warranty_repair_annual: 0,
    warranty_remaining_months: 0,
  };
}

export function defaultEmotionalScores(): EmotionalScores {
  return {
    driving_enjoyment: 3.0,
    powertrain_character: 3.0,
    practical_utility: 3.0,
    winter_confidence: 3.0,
    brand_trust: 3.0,
    status_prestige: 3.0,
    ownership_pride: 3.0,
    technology_features: 3.0,
  };
}

export function defaultEmotionalWeights(): EmotionalWeights {
  return {
    driving_enjoyment: 1.0,
    powertrain_character: 1.0,
    practical_utility: 1.0,
    winter_confidence: 1.0,
    brand_trust: 1.0,
    status_prestige: 1.0,
    ownership_pride: 1.0,
    technology_features: 1.0,
  };
}

export function defaultEmotionalInputs(): EmotionalInputs {
  return {
    scores: defaultEmotionalScores(),
    weights: defaultEmotionalWeights(),
  };
}

export function defaultCapitalAllocationInputs(): CapitalAllocationInputs {
  return {
    opportunity_cost_rate: 0.07,
    analysis_horizon_years: 5,
    include_opportunity_cost: true,
  };
}

export function defaultVehicleInput(id?: string): VehicleInput {
  return {
    vehicle_id: id ?? crypto.randomUUID(),
    label: 'New Vehicle',
    model_year: new Date().getFullYear(),
    purchase_condition: 'new',
    vehicle_type: null,
    depreciation: defaultDepreciationInputs(),
    financing: defaultFinancingInputs(),
    operating_costs: defaultOperatingCostInputs(),
    emotional: defaultEmotionalInputs(),
    capital_allocation: defaultCapitalAllocationInputs(),
    upfit_cost: 0,
    upfit_description: '',
    vocation_profile: null,
    loaded_hourly_labor_rate: 0,
    weekly_hours: 0,
    weeks_per_year: 50,
    operating_days_per_week: 5,
    drivers_per_vehicle: 1,
    ownership_period_years: 5.0,
    annual_miles_per_vehicle: null,
    daily_idle_hours_override: null,
    mpg_data_source: null,
    electricity_rate_per_kwh: 0.16,
    diesel_price_per_gallon: 4.00,
    charging_infrastructure_cost: 0,
    purchase_incentives: 0,
    expected_residual_value: null,
    residual_data_source: null,
    depreciation_curve_id: null,
    sec179_elected: true,
    btb_warranty_years: null,
    btb_warranty_miles: null,
    powertrain_warranty_years: null,
    powertrain_warranty_miles: null,
    annual_repair_cost: null,
    custom_annual_cost: null,
    custom_annual_cost_label: null,
  };
}

export function defaultScenarioInput(): ScenarioInput {
  return {
    scenario_id: null,
    name: 'New Scenario',
    description: null,
    horizon_months: 60,
    annual_miles: 12000,
    financial_weight: 0.70,
    emotional_weight: 0.30,
    dominance_epsilon_pct: 0.25,
    use_emotional_override: false,
    investment_return_rate: 0.07,
    sales_tax_rate: 0.06,
    winter_miles_pct: 0.40,
    state_code: 'MI',
    vehicles: [],
    mode: 'individual',
    fleet_name: null,
    line_items: null,
    scenario_fuel_cost_per_gallon: null,
    scenario_diesel_price_per_gallon: null,
    scenario_electricity_rate_per_kwh: null,
    marginal_tax_rate: 0.21,
    business_use_pct: 1.0,
    tax_year: 2026,
    sec179_limit: 1_160_000,
  };
}

export function defaultLineItem(): LineItem {
  return {
    vehicle: defaultVehicleInput(),
    quantity: 1,
    line_item_name: null,
  };
}
